'use client';

import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ContactFormData } from '@workspace/interfaces/contact-form.types';
import { generateContactFormSchema } from '@workspace/schema/contact-form.schema';
import type { IFormSubmissionResult } from '../interfaces';
import type { ContactFormFieldConfig } from '../interfaces';
import { FORM_FIELD_KINDS } from '@workspace/constants/contact-form.constant';
import { createHubSpotSubmissionData, getHubSpotCookie } from '@/app/lib/hubspot-utils';
import { GENERIC_HUBSPOT_ERROR_MESSAGE } from '@workspace/constants/contact-form.constant';
import { useHubSpot } from '@workspace/hooks/useHubSpot';
import { pushFormEvent } from '@workspace/lib/gtm-tracking';
import type { HubSpotSubmissionData } from '@workspace/interfaces';
import { sanitizePhone } from '@workspace/lib/helper';

/**
 * Builds a HubSpot submission payload from arbitrary form values.
 * Used in dynamic mode when the field list comes from the HubSpot API
 * rather than the hardcoded ContactFormData type.
 *
 * Each field's current value is stringified and sent as a HubSpot field.
 * Honeypot and empty optional values are excluded.
 */
/**
 * Uploads a File to HubSpot via our server-side proxy and returns the public URL.
 * Throws if the upload fails so the caller can surface the error to the user.
 */
async function uploadFileToHubSpot(file: File): Promise<string> {
  const body = new FormData();
  body.append('file', file, file.name);

  const res = await fetch('/api/hubspot/upload-file', { method: 'POST', body });
  const data = (await res.json()) as { success: boolean; url?: string; message?: string };

  if (!res.ok || !data.success || !data.url) {
    throw new Error(data.message ?? 'File upload failed.');
  }
  return data.url;
}

async function buildDynamicSubmissionData(
  values: Record<string, unknown>,
  fields: ReadonlyArray<ContactFormFieldConfig>,
): Promise<HubSpotSubmissionData> {
  const hutk = getHubSpotCookie();

  const submissionFields = await Promise.all(
    fields
      .filter((f) => String(f.name) !== 'honeypot')
      .map(async (f) => {
        const raw = values[String(f.name)];
        let value: string;
        if (f.kind === FORM_FIELD_KINDS.CHECKBOX) {
          // HubSpot booleancheckbox fields expect the string literal "true" or "false"
          value = raw ? 'true' : 'false';
        } else if (f.kind === FORM_FIELD_KINDS.FILE) {
          // Upload the File object first; use the returned URL as the field value.
          const file = raw as File | null;
          value = file ? await uploadFileToHubSpot(file) : '';
        } else if (
          f.kind === FORM_FIELD_KINDS.INPUT &&
          (String(f.name) === 'phone' || String(f.name) === '0-2/phone')
        ) {
          // Send digits-only phone to HubSpot — strip AU display formatting
          const sanitized = sanitizePhone(String(raw ?? ''));
          // If HubSpot expects country code, format local '04..' entries to '+61...'
          value = sanitized.startsWith('0') ? `+61${sanitized.substring(1)}` : sanitized;
        } else {
          value = String(raw ?? '');
        }

        const submitName = String(f.name);
        return { name: submitName, value };
      }),
  );

  const submission: HubSpotSubmissionData = { fields: submissionFields };

  if (hutk) {
    submission.context = {
      hutk,
      pageUri: typeof window !== 'undefined' ? window.location.href : '',
      pageName: typeof document !== 'undefined' ? document.title : '',
    };
  }

  return submission;
}

interface UseContactFormOptions {
  /** Override the default HubSpot form GUID. */
  formId?: string;
  /**
   * When provided, the form operates in "dynamic mode".
   * Submission data is built from all form values instead of the
   * hardcoded ContactFormData mapper, so any custom HubSpot fields work.
   */
  fields?: ReadonlyArray<ContactFormFieldConfig>;
  /** Custom thank-you message to display after a successful submission. */
  thankYouMessage?: string;
}

export function useContactForm({ formId, fields, thankYouMessage }: UseContactFormOptions = {}) {
  const isDynamic = !!fields && fields.length > 0;
  const [result, setResult] = useState<IFormSubmissionResult | null>(null);
  const [messageLength, setMessageLength] = useState(0);
  const [showForm, setShowForm] = useState(true);
  const { submitToHubSpot, loading } = useHubSpot({ formId });

  const schema = useMemo(() => generateContactFormSchema(fields), [fields]);
  const form = useForm<ContactFormData>({
    resolver: zodResolver(schema as any),
    mode: 'onBlur',
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      company: '',
      state: '',
      area_of_work: '',
      where_did_you_hear_about_porta_: '',
      enquiry_type___dropdown: '',
      message: '',
      newsletter: false,
      honeypot: '',
    },
  });

  // When fields change (dynamic mode: async fetch completes), reset so the
  // form registers the new field names with correct empty default values.
  useEffect(() => {
    if (isDynamic && fields) {
      const dynamicDefaults = Object.fromEntries(
        fields.map((f) => [
          String(f.name),
          f.kind === FORM_FIELD_KINDS.CHECKBOX ? false : f.kind === FORM_FIELD_KINDS.FILE ? null : '',
        ]),
      ) as unknown as ContactFormData;
      form.reset({ ...dynamicDefaults, honeypot: '' });
    }
  }, [fields]);

  const handleMessageChange = (value: string): string => {
    setMessageLength(value.length);
    return value;
  };

  const handleInvalidSubmit = (): void => {
    pushFormEvent('form_submission_error', {
      form_name: 'contact_form',
      form_location: typeof window !== 'undefined' ? window.location.pathname : '',
      error_type: 'validation_error',
    });
  };

  const onSubmit = async (values: ContactFormData): Promise<void> => {
    setResult(null);

    if (values.honeypot && values.honeypot.trim() !== '') {
      return;
    }

    try {
      // Dynamic mode: build submission from all form values generically
      // so any HubSpot field name is included without needing a typed mapper.
      const submissionData =
        isDynamic && fields
          ? await buildDynamicSubmissionData(values as unknown as Record<string, unknown>, fields)
          : createHubSpotSubmissionData(values);
      const response = await submitToHubSpot(submissionData);

      if (response.success) {
        pushFormEvent('form_submission', {
          form_name: 'contact_form',
          form_location: typeof window !== 'undefined' ? window.location.pathname : '',
          newsletter_opt_in: values.newsletter,
        });
        setResult({
          inlineMessage:
            thankYouMessage ?? 'Thank you for your enquiry.\nOne of our team member will be in contact with you soon.',
        });

        // Hide form on successful submission
        setShowForm(false);
      } else {
        pushFormEvent('form_submission_error', {
          form_name: 'contact_form',
          form_location: typeof window !== 'undefined' ? window.location.pathname : '',
          error_type: 'api_error',
        });
        setResult({
          error: true,
          inlineMessage: GENERIC_HUBSPOT_ERROR_MESSAGE,
        });
      }
    } catch (err) {
      console.error('Form submission error:', err);
      pushFormEvent('form_submission_error', {
        form_name: 'contact_form',
        form_location: typeof window !== 'undefined' ? window.location.pathname : '',
        error_type: 'api_error',
      });
      setResult({
        error: true,
        inlineMessage: GENERIC_HUBSPOT_ERROR_MESSAGE,
      });
    }
  };

  return {
    form,
    loading,
    result,
    messageLength,
    showForm,
    handleMessageChange,
    handleInvalidSubmit,
    onSubmit,
  };
}
